import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Code as CodeIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  WrapText as WrapIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import {
  borderRadius,
  getSemanticSurfaceTokens,
  layoutGap,
} from '../../../../theme/theme';
import {
  useIronPythonValidation,
  type IronPythonValidationModel,
} from '../../hooks/useIronPythonValidation';
import IronPythonCodeEditor, { type IronPythonCodeEditorHandle } from './IronPythonCodeEditor';
import PythonScriptEditorSidebar from './PythonScriptEditorSidebar';

const StarterScript = `from Autodesk.Revit.DB import StorageType

def get_elem_property(elem):
    parameter = elem.LookupParameter("Parameter name")
    if not parameter or not parameter.HasValue:
        return ""

    return parameter.AsString() or ""`;

interface PythonScriptEditorDialogProps {
  open: boolean;
  attributeName: string;
  objectType: string;
  initialScript: string;
  onClose: () => void;
  onSave: (script: string) => void;
}

interface PythonScriptEditorErrorBoundaryProps {
  children: React.ReactNode;
  onClose: () => void;
  resetKey: string;
}

interface PythonScriptEditorErrorBoundaryState {
  hasError: boolean;
}

export class PythonScriptEditorErrorBoundary extends React.Component<
  PythonScriptEditorErrorBoundaryProps,
  PythonScriptEditorErrorBoundaryState
> {
  public state: PythonScriptEditorErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): PythonScriptEditorErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidUpdate(previousProps: PythonScriptEditorErrorBoundaryProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  public render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Box
        role="alert"
        sx={{
          p: 2,
          border: 1,
          borderColor: 'error.main',
          borderRadius: `${borderRadius.md}px`,
          backgroundColor: 'background.paper',
        }}
      >
        <Typography fontWeight={700}>IronPython formula editor could not open</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          Your PCF mapping is unchanged. Close the editor, then reopen the formula.
        </Typography>
        <Button variant="outlined" color="error" onClick={this.props.onClose}>
          Close formula editor
        </Button>
      </Box>
    );
  }
}

interface EditorHeaderProps {
  attributeName: string;
  objectType: string;
  isNarrowScreen: boolean;
  onClose: () => void;
}

const EditorHeader: React.FC<EditorHeaderProps> = ({
  attributeName,
  objectType,
  isNarrowScreen,
  onClose,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);

  return (
    <DialogTitle
      component="div"
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 1.75,
        borderBottom: `1px solid ${colors.border.default}`,
        backgroundColor: colors.background.elevated,
      }}
    >
      <Stack direction="row" spacing={layoutGap.rack} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 auto',
            color: theme.palette.primary.light,
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
            borderRadius: `${borderRadius.md}px`,
          }}
        >
          <CodeIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" flexWrap="wrap">
            <Typography id="python-script-editor-title" component="h2" variant="h6" fontWeight={700}>
              IronPython formula editor
            </Typography>
            <Chip label="IronPython" size="small" variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap={!isNarrowScreen}>
             Produces <strong>{attributeName || 'an unnamed PCF attribute'}</strong> for the{' '}
            <strong>{objectType || 'current object type'}</strong> mapping scope.
          </Typography>
        </Box>
         <IconButton aria-label="Close IronPython formula editor" onClick={onClose} edge="end">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </DialogTitle>
  );
};

interface EditorToolbarProps {
  isEmpty: boolean;
  hasUnsavedChanges: boolean;
  wrapLines: boolean;
  onInsertStarter: () => void;
  onReset: () => void;
  onToggleWrap: () => void;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  isEmpty,
  hasUnsavedChanges,
  wrapLines,
  onInsertStarter,
  onReset,
  onToggleWrap,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={layoutGap.row}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      sx={{
        flexShrink: 0,
        minHeight: 44,
        px: 1.25,
        py: 0.75,
        borderBottom: `1px solid ${colors.border.default}`,
        backgroundColor: colors.background.elevated,
      }}
    >
      <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" sx={{ flex: 1 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 7,
            height: 7,
            borderRadius: `${borderRadius.circle}`,
            backgroundColor: hasUnsavedChanges
              ? theme.palette.warning.main
              : theme.palette.success.main,
          }}
        />
        <Typography variant="body2" fontWeight={700}>formula.py</Typography>
        <Typography variant="caption" color="text.secondary">
          {hasUnsavedChanges ? 'Formula has unsaved changes' : 'Formula matches the saved mapping'}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={layoutGap.dense} alignItems="center">
         {isEmpty && <Button size="small" onClick={onInsertStarter}>Insert starter formula</Button>}
        <Button
          size="small"
          startIcon={<WrapIcon fontSize="small" />}
          aria-label={wrapLines ? 'Disable line wrapping' : 'Enable line wrapping'}
          aria-pressed={wrapLines}
          onClick={onToggleWrap}
        >
          Wrap lines
        </Button>
        <Button
          size="small"
          startIcon={<ResetIcon fontSize="small" />}
          disabled={!hasUnsavedChanges}
          onClick={onReset}
        >
          Reset formula
        </Button>
      </Stack>
    </Stack>
  );
};

interface EditorWorkspaceProps {
  draft: string;
  initialScript: string;
  isNarrowScreen: boolean;
  metrics: string;
  validation: IronPythonValidationModel;
  wrapLines: boolean;
  editorRef: React.RefObject<IronPythonCodeEditorHandle | null>;
  onChange: (script: string) => void;
  onSave: () => void;
  onToggleWrap: () => void;
}

const EditorWorkspace: React.FC<EditorWorkspaceProps> = ({
  draft,
  initialScript,
  isNarrowScreen,
  metrics,
  validation,
  wrapLines,
  editorRef,
  onChange,
  onSave,
  onToggleWrap,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const hasUnsavedChanges = draft !== initialScript;

  return (
    <Box
      sx={{
        minWidth: 0,
        minHeight: { xs: 'auto', md: 0 },
        height: { md: '100%' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${colors.border.strong}`,
        borderRadius: `${borderRadius.md}px`,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <EditorToolbar
        isEmpty={draft.length === 0}
        hasUnsavedChanges={hasUnsavedChanges}
        wrapLines={wrapLines}
        onInsertStarter={() => onChange(StarterScript)}
        onReset={() => onChange(initialScript)}
        onToggleWrap={onToggleWrap}
      />
      <IronPythonCodeEditor
        ref={editorRef}
        value={draft}
        isNarrowScreen={isNarrowScreen}
        wrapLines={wrapLines}
        diagnostics={validation.diagnostics}
        errorCount={validation.errorCount}
        warningCount={validation.warningCount}
        runtimeStatus={validation.runtimeStatus}
        runtimeError={validation.runtimeError}
        onChange={onChange}
        onSave={onSave}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 0.75,
          borderTop: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated,
        }}
      >
         <Typography variant="caption" color="text.secondary">IronPython 3.4.2 syntax · UTF-8</Typography>
        <Typography variant="caption" color="text.secondary">{metrics}</Typography>
      </Stack>
    </Box>
  );
};

interface EditorActionsProps {
  hasErrors: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: () => void;
}

const EditorActions: React.FC<EditorActionsProps> = ({ hasErrors, isSaving, onClose, onSave }) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);

  return (
    <DialogActions
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 1.25,
        gap: 0.5,
        borderTop: `1px solid ${colors.border.default}`,
        backgroundColor: colors.background.elevated,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'none', sm: 'block' }, mr: 'auto' }}
      >
         Press Ctrl+Enter or Cmd+Enter to validate and save the formula.
      </Typography>
      <Button onClick={onClose} sx={{ whiteSpace: 'nowrap' }}>Cancel</Button>
      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        disabled={hasErrors || isSaving}
        onClick={onSave}
        sx={{ minWidth: 112, whiteSpace: 'nowrap' }}
      >
         {isSaving ? 'Validating formula…' : 'Save formula'}
      </Button>
    </DialogActions>
  );
};

const PythonScriptEditorDialog: React.FC<PythonScriptEditorDialogProps> = ({
  open,
  attributeName,
  objectType,
  initialScript,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactLayout = useMediaQuery(theme.breakpoints.down('md'));
  const [draft, setDraft] = useState(initialScript);
  const [wrapLines, setWrapLines] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef<IronPythonCodeEditorHandle | null>(null);
  const validation = useIronPythonValidation(draft);

  useEffect(() => {
    if (open) {
      setDraft(initialScript);
      setWrapLines(true);
      setIsSaving(false);
    }
  }, [initialScript, open]);

  const metrics = useMemo(() => {
    const lineCount = draft.length === 0 ? 0 : draft.split(/\r\n|\r|\n/).length;
    return `${lineCount} ${lineCount === 1 ? 'line' : 'lines'} · ${draft.length} characters`;
  }, [draft]);

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const result = await validation.validateForSave();
      if (result.canSave) {
        onSave(result.source);
        return;
      }
      const firstError = result.diagnostics.find(({ severity }) => severity === 'error');
      if (firstError) {
        editorRef.current?.focusDiagnostic(firstError);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isNarrowScreen}
      maxWidth="xl"
      aria-labelledby="python-script-editor-title"
      PaperProps={{
        sx: {
          height: isNarrowScreen ? '100%' : 'min(820px, 94vh)',
          borderRadius: isNarrowScreen ? 0 : `${borderRadius.lg}px`,
          overflow: 'hidden',
        },
      }}
    >
      <EditorHeader
        attributeName={attributeName}
        objectType={objectType}
        isNarrowScreen={isNarrowScreen}
        onClose={onClose}
      />
      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          display: { xs: 'block', md: 'grid' },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 280px' },
          gridTemplateRows: { md: 'minmax(0, 1fr)' },
          gap: { xs: 1.5, md: 2 },
          alignItems: 'stretch',
          minHeight: 0,
          overflowY: { xs: 'auto', md: 'hidden' },
          backgroundColor: colors.background.panel,
        }}
      >
        <EditorWorkspace
          draft={draft}
          initialScript={initialScript}
          isNarrowScreen={isNarrowScreen}
          metrics={metrics}
          validation={validation}
          wrapLines={wrapLines}
          editorRef={editorRef}
          onChange={setDraft}
          onSave={handleSave}
          onToggleWrap={() => setWrapLines((current) => !current)}
        />
        <PythonScriptEditorSidebar
          diagnostics={validation.diagnostics}
          runtimeStatus={validation.runtimeStatus}
          runtimeError={validation.runtimeError}
          isCompactLayout={isCompactLayout}
          onSelectDiagnostic={(diagnostic) => editorRef.current?.focusDiagnostic(diagnostic)}
        />
      </DialogContent>
      <EditorActions
        hasErrors={validation.errorCount > 0}
        isSaving={isSaving}
        onClose={onClose}
        onSave={handleSave}
      />
    </Dialog>
  );
};

const SafePythonScriptEditorDialog: React.FC<PythonScriptEditorDialogProps> = (props) => (
  <PythonScriptEditorErrorBoundary
    resetKey={`${props.open}:${props.attributeName}:${props.objectType}`}
    onClose={props.onClose}
  >
    <PythonScriptEditorDialog {...props} />
  </PythonScriptEditorErrorBoundary>
);

export default React.memo(SafePythonScriptEditorDialog);
